import React, { type FC } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';

import {
	BannerHeightReader,
	type BannerHeightState,
} from '@confluence/banners/entry-points/BannerHeightReader.ts';
import { usePageRecommendationData } from '@confluence/page-recommendations/entry-points/usePageRecommendationData.tsx';

export const VIEW_PAGE_MAIN_CONTENT_CONTAINER_TEST_ID = 'view-page-main-content-container';

const styles = cssMap({
	container: {
		display: 'flow-root',
	},
});

type ViewPageMainContentContainerProps = {
	children?: React.ReactNode;
	isFabricPage?: boolean;
	isEmbeddedPage?: boolean;
};

export const ViewPageMainContentContainer: FC<ViewPageMainContentContainerProps> = ({
	children,
	isFabricPage,
	isEmbeddedPage,
}) => {
	// Set minHeight for all Page content above footer comments, so that footer comments
	// are always placed at the bottom of viewport when Page content is short.
	// This CSS-based method is compatible with SSR. So prefer this over dynamically
	// calculating spacing using window.innerHeight, which is not available in SSR.

	// If page has EOP recommendations, don't add any spacing between it and footer
	// comments. So don't set minHeight.
	// If page is a legacy TinyMCE page (i.e. !isFabricPage), don't add spacing either.
	// If page is within an embedded confluence iframe, don't set minHeight to avoid infinite height growth.
	const { hasRecs } = usePageRecommendationData();
	const setMinHeight = Boolean(isFabricPage && !hasRecs && !isEmbeddedPage);

	if (setMinHeight) {
		return (
			<BannerHeightReader>
				{(bannerState: BannerHeightState) => {
					// - bannerHeight includes top nav bar and any banner above it.
					// - ContentContainerComponent has a 46px padding top.
					// - ObjectAddCommentContainer has a 64px height.
					// - We lift footer comment top by 14px to make it visually distinct
					//   when a short Page has some vs zero existing footer comment.
					//   (This matches the spacing in LiveDoc.)
					const bannerHeight = bannerState.getTotalHeight();
					const contentContainerPaddingTop = 46;
					const addFooterCommentHeight = 64;
					const extraFooterCommentHeightToShow = 14;
					return (
						<Box
							xcss={styles.container}
							style={{
								minHeight: `calc(100vh - ${bannerHeight}px - ${contentContainerPaddingTop}px - ${addFooterCommentHeight}px - ${extraFooterCommentHeightToShow}px)`,
							}}
							testId={VIEW_PAGE_MAIN_CONTENT_CONTAINER_TEST_ID}
							data-vc={VIEW_PAGE_MAIN_CONTENT_CONTAINER_TEST_ID}
						>
							{children}
						</Box>
					);
				}}
			</BannerHeightReader>
		);
	}

	return (
		<Box
			testId={VIEW_PAGE_MAIN_CONTENT_CONTAINER_TEST_ID}
			data-vc={VIEW_PAGE_MAIN_CONTENT_CONTAINER_TEST_ID}
		>
			{children}
		</Box>
	);
};
